import { XCircleIcon } from '@heroicons/react/24/solid'
import { Tooltip } from '@heroui/react'
import { ModelControlEnum } from '@qse/constants'
import { useActiveModelStore } from '@qse/stores'
import { useIsOverQuota } from '@qse/stores'
import { formatFileSize, truncateTextMiddle } from '@qse/utils'
import { MoreVertical, Star } from 'lucide-react'
import React from 'react'

import { FileTypeIcon } from '../file-type'
import type { FileItem } from '../hooks/useFileManagment'
import {
  CopyIconDrop,
  DeleteIcon,
  DownloadIconDrop,
  FolderIcon,
  MoveIcon,
  RenameIcon,
  StarIconMenu,
} from '../icons'
function formatDate(date: string): string {
  const d = new Date(date)

  if (isNaN(d.getTime())) return date

  return (
    d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  )
}

// Helper function to extract the immediate parent folder name
function getParentFolderName(path?: string): { folderName: string; fullPath: string } {
  if (!path) return { folderName: '', fullPath: '' }

  // Remove trailing slash if present
  const cleanPath = path.endsWith('/') ? path.slice(0, -1) : path

  // Get everything before the last slash (full folder path)
  const lastSlashIndex = cleanPath.lastIndexOf('/')
  const fullFolderPath = lastSlashIndex !== -1 ? cleanPath.substring(0, lastSlashIndex) : ''

  // Get the immediate parent folder name (last segment of the folder path)
  const lastFolderSlashIndex = fullFolderPath.lastIndexOf('/')
  const folderName =
    lastFolderSlashIndex !== -1
      ? fullFolderPath.substring(lastFolderSlashIndex + 1)
      : fullFolderPath

  // Extract relative path (remove user domain part)
  const relativePath = extractRelativePath(fullFolderPath)

  return {
    folderName: folderName || '',
    fullPath: relativePath,
  }
}

// Helper function to truncate text with ellipsis in the middle
function truncateText(text: string, maxLength: number): string {
  return truncateTextMiddle(text, maxLength)
}

// Helper function to extract relative path from full path
function extractRelativePath(fullPath: string): string {
  if (!fullPath) return ''

  // Find the pattern /username@domain/ and get everything after it
  const domainPattern = /\/[^/]+@[^/]+\//
  const match = fullPath.match(domainPattern)

  if (match) {
    // Get the part after the domain pattern
    const domainEndIndex = fullPath.indexOf(match[0]) + match[0].length - 1

    return fullPath.substring(domainEndIndex)
  }

  // If no domain pattern found, return the original path
  return fullPath
}

// Custom tooltip component for showing full content
const PathTooltip: React.FC<{
  children: React.ReactNode
  fullContent: string
  title?: string
  wide?: boolean
}> = ({ children, fullContent }) => {
  if (!fullContent) {
    return <>{children}</>
  }

  return (
    <Tooltip
      offset={0}
      content={
        <div className="w-fit break-words w-full" onClick={e => e.stopPropagation()}>
          {fullContent}
        </div>
      }
    >
      {children}
    </Tooltip>
  )
}

interface FileCardProps {
  file: FileItem
  isSelected: boolean
  isDragOver: boolean
  isDragging: boolean
  isFolderDrop?: boolean
  draggedFiles?: (string | number)[] // Add draggedFiles prop
  onClick: (e: React.MouseEvent) => void
  onDoubleClick?: () => void
  onContextMenu: (e: React.MouseEvent, path?: string) => void
  onDragStart: (e: React.DragEvent, fileId?: string | number) => void
  onDragOver: (e: React.DragEvent) => void
  onDragEnter: (e: React.DragEvent) => void
  onDragLeave: () => void
  onDrop: (e: React.DragEvent) => void
  onDragEnd: () => void
  innerRef: (el: HTMLDivElement | null) => void
  variant?: 'grid' | 'list'
  onSelectionChange?: (fileId: string | number, selected: boolean) => void
  // Action handlers
  onDownload?: (file: FileItem) => void
  onRename?: (file: FileItem) => void
  onMove?: (file: FileItem) => void
  onCopy?: (file: FileItem) => void
  onToggleFavorite?: (file: FileItem) => void
  onDelete?: (file: FileItem) => void
  onRestore?: (file: FileItem) => Promise<void> // Add restore handler
  onDeleteForever?: (file: FileItem) => Promise<void> // Add delete forever handler
  trashMode?: boolean // Add trashMode prop
  onDeleteGhostFiles?: (fileIds: string[]) => Promise<void> // Add delete ghost files handler
}

export const ListViewHeader: React.FC<{ trashMode?: boolean }> = ({ trashMode = false }) => {
  return (
    <div
      className={`hidden md:grid grid-cols-12 py-3 items-center rounded-2xl px-4 w-full bg-input-light dark:bg-[#232324] sticky top-0 z-50`}
    >
      {/* Name */}
      <div className="text-foreground/90 dark:text-white/90 text-sm font-medium truncate col-span-5">
        Name
      </div>

      {/* Date */}
      <div className="text-foreground/90 dark:text-white/90 text-sm font-medium truncate col-span-2">
        {trashMode ? 'Date Trashed' : 'Created'}
      </div>

      {/* Size */}
      <div className="text-foreground/90 dark:text-white/90 text-sm font-medium truncate col-span-2">
        Size
      </div>

      {/* Original Parent (trash only) */}
      {trashMode && (
        <div className="text-foreground/90 dark:text-white/90 text-sm font-medium truncate col-span-2">
          Original Parent
        </div>
      )}

      {/* Actions spacer */}
      <div />
    </div>
  )
}

export const FileCard: React.FC<FileCardProps> = ({
  file,
  isSelected,
  isDragOver,
  isDragging,
  isFolderDrop,
  draggedFiles = [],
  innerRef,
  variant = 'grid',
  onSelectionChange,
  onClick,
  onDoubleClick,
  onContextMenu,
  onDownload,
  onRename,
  onMove,
  onCopy,
  onToggleFavorite,
  onDelete,
  onRestore,
  onDeleteForever,
  trashMode = false,
  onDeleteGhostFiles,
  ...rest
}) => {
  const isOverQuota = useIsOverQuota()
  // Helper to check if file is interactable
  // Ghost files (UPLOADING/ENCRYPTING/FAILED) should be interactable for selection and context menu
  // System folders have different interactability rules
  const isSystemFolder = file.systemFolderType
  const isInteractable =
    (file.status === 'AVAILABLE' && file.isEncrypted !== false) ||
    file.status === 'UPLOADING' ||
    file.status === 'ENCRYPTING' ||
    file.status === 'FAILED'
  // Show "-" for folders in list view only, otherwise format the file size
  const fileSize = file.isDir ? '-' : formatFileSize(file.size)
  // Use trashedAt for trash mode, createdAt for normal mode
  const dateToShow = trashMode && file.trashedAt ? file.trashedAt : file.createdAt
  const formattedDate = formatDate(dateToShow)

  // Helper to check if trying to drop folder into itself
  const isDroppingFolderIntoItself = file.isDir && draggedFiles.includes(file.id)

  // Should show drop overlay (dashed border) - only if folder, dragging over, and not dropping into itself
  const shouldShowDropOverlay = isDragOver && file.isDir && !isDroppingFolderIntoItself
  const handleClick = (e: React.MouseEvent) => {
    // Prevent selection of CHAT_ROOT folders (except in trash mode)
    if (file.systemFolderType === 'CHAT_ROOT' && !trashMode) {
      e.preventDefault()
      e.stopPropagation()

      return
    }
    // Allow click for selection even for unavailable files
    onClick?.(e)
  }

  const handleDoubleClick = () => {
    if (!isInteractable) return
    onDoubleClick?.()
  }

  const handleContextMenu = (e: React.MouseEvent, path?: string) => {
    if (!isInteractable) {
      e.preventDefault()
      e.stopPropagation()

      return
    }
    // Prevent context menu for CHAT_ROOT folders (except in trash mode)
    if (file.systemFolderType === 'CHAT_ROOT' && !trashMode) {
      e.preventDefault()
      e.stopPropagation()

      return
    }
    onContextMenu(e, path)
  }

  const closeAllModals = useActiveModelStore(state => state.closeAllModals)

  // Enhanced drag state styling
  const getDragStateClasses = () => {
    if (isDragging) {
      return 'opacity-50 scale-95 transform-gpu'
    }

    return ''
  }

  // Enhanced selection state styling
  const getSelectionStateClasses = () => {
    if (isSelected) {
      return 'border-accent shadow-lg shadow-accent/20 bg-accent-light dark:border-[#4793EB] dark:shadow-blue-400/20 dark:bg-blue-500/5'
    }

    return 'border-border hover:bg-muted-light dark:border-transparent dark:hover:bg-gray-700'
  }

  // Folder drop zone styling when dragging files
  const getFolderDropZoneClasses = () => {
    if (file.isDir && isDragOver && !isDragging && !isDroppingFolderIntoItself) {
      return 'border-accent border-dashed bg-accent-light/50 dark:border-blue-400 dark:bg-blue-500/10 dark:shadow-blue-400/20'
    }

    return ''
  }

  // Inline Lock Icon
  const LockIcon = ({
    className = 'w-8 h-8 text-gray-400 dark:text-gray-500',
  }: {
    className?: string
  }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <rect height="8" rx="2" stroke="currentColor" strokeWidth="2" width="14" x="5" y="11" />
      <path d="M8 11V8a4 4 0 1 1 8 0v3" stroke="currentColor" strokeWidth="2" />
      <circle cx="12" cy="15" fill="currentColor" r="1.5" />
    </svg>
  )

  // Processing Overlay Component
  const ProcessingOverlay = ({
    status,
    isEncrypted,
  }: {
    status: string | undefined
    isEncrypted?: boolean
  }) => {
    const statusMessages = {
      PROCESSING: 'Processing...',
      IN_PROGRESS: 'Processing...',
      ENCRYPTING: 'Encrypting...',
      UPLOADING: 'Uploading...',
      FAILED: 'FAILED',
    } as const

    // Determine text color based on status
    const getStatusTextColor = () => {
      if (status === 'FAILED') {
        return 'text-red-600 dark:text-[#DD524C]'
      }

      return 'text-gray-600 dark:text-gray-300'
    }

    // Get the message to display
    const getMessage = () => {
      if (isEncrypted === false) {
        return 'Not Encrypted'
      }

      if (status && status in statusMessages) {
        return statusMessages[status as keyof typeof statusMessages]
      }

      return 'Available...'
    }

    return (
      <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white/30 dark:bg-black/20 backdrop-blur-[0.8px] rounded-xl">
        <LockIcon className="w-6 h-6 text-gray-400 dark:text-gray-500 mb-1" />
        <span className={`text-xs font-medium ${getStatusTextColor()}`}>{getMessage()}</span>
      </div>
    )
  }

  if (variant === 'list') {
    // Custom ref callback for list view to handle both desktop and mobile elements
    const handleListRef = (el: HTMLDivElement | null) => {
      if (el) {
        // Check if this element is currently visible by checking computed styles
        if (typeof window !== 'undefined') {
          const computedStyle = window.getComputedStyle(el)
          const isVisible = computedStyle.display !== 'none'

          if (isVisible) {
            innerRef(el)
          }
        } else {
          // Fallback for SSR - register the element anyway
          innerRef(el)
        }
      } else {
        innerRef(null)
      }
    }

    return (
      <>
        {/* Desktop/tablet view: columns */}
        <div
          ref={handleListRef}
          className={`file-card group  grid-cols-12 items-center rounded-2xl px-4 py-2 w-full bg-input-light dark:bg-[#323232] transition-all duration-200 border-2 hidden lg:grid relative ${file.systemFolderType === 'CHAT_ROOT' ? 'opacity-60' : 'opacity-100'} ${getSelectionStateClasses()} ${getDragStateClasses()} ${getFolderDropZoneClasses()} ${!isInteractable ? 'select-none opacity-70' : ''} ${file.status === 'UPLOADING' || file.status === 'ENCRYPTING' || file.status === 'FAILED' ? 'opacity-80' : ''} responsive-list-row`}
          draggable={isInteractable && !isSystemFolder}
          role="button"
          style={{ minHeight: 56 }}
          tabIndex={isInteractable ? 0 : -1}
          onClick={handleClick}
          onDoubleClick={handleDoubleClick}
          onKeyDown={e => {
            if (!isInteractable && (e.key === ' ' || e.key === 'Enter')) {
              e.preventDefault()
              e.stopPropagation()
            }
          }}
          onContextMenu={e => handleContextMenu(e, file.path)}
          // If selection is handled via props, block it for unavailable files
          onMouseDown={e => {
            if (!isInteractable) {
              e.preventDefault()
              e.stopPropagation()
            }
          }}
          {...rest}
        >
          {/* Processing overlay for unavailable files (excluding ghost files) */}
          {(file.status !== 'AVAILABLE' || file.isEncrypted === false) &&
            file.status !== 'UPLOADING' &&
            file.status !== 'ENCRYPTING' &&
            file.status !== 'FAILED' &&
            !file.isDir && (
              <ProcessingOverlay isEncrypted={file.isEncrypted} status={file.status} />
            )}

          {/* Name */}
          <div className="flex-1 flex items-center gap-4 min-w-0 col-span-5">
            {file.isDir ? (
              <FolderIcon className="sm:w-[30px] sm:h-[30px]" />
            ) : (
              <FileTypeIcon
                className="sm:w-[30px] sm:h-[30px]"
                filename={file.name}
                mediaType={file.mediaType}
                mimeType={file.mimeType}
                size={24}
              />
            )}
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <PathTooltip fullContent={file.name}>
                <h3 className="text-foreground dark:text-white text-base font-medium truncate block overflow-hidden text-ellipsis whitespace-nowrap">
                  {file.name}
                </h3>
              </PathTooltip>

              {file.isFavorite && (
                <div className="flex align-top">
                  <StarIconMenu className="w-4 h-4 text-yellow-400 fill-current flex-shrink-0 inline ml-1" />
                </div>
              )}
              {/* Status badge for ghost files - positioned inside Name column (exclude directories) */}
              {(file.status === 'UPLOADING' ||
                file.status === 'ENCRYPTING' ||
                file.status === 'FAILED') &&
                !file.isDir && (
                  <div className="flex items-center gap-1 px-2 py-1 rounded-md flex-shrink-0">
                    <div
                      className={`w-2 h-2 rounded-full ${
                        file.status === 'UPLOADING'
                          ? 'bg-blue-500 dark:bg-blue-400 animate-pulse'
                          : file.status === 'ENCRYPTING'
                            ? 'bg-cyan-500 dark:bg-cyan-400 animate-pulse'
                            : 'bg-red-500 dark:bg-red-400'
                      }`}
                    />
                    <span
                      className={`text-xs font-medium ${
                        file.status === 'UPLOADING'
                          ? 'text-blue-600 dark:text-blue-300'
                          : file.status === 'ENCRYPTING'
                            ? 'text-cyan-600 dark:text-cyan-300'
                            : 'text-red-600 dark:text-red-300'
                      }`}
                    >
                      {file.status === 'UPLOADING'
                        ? 'Uploading...'
                        : file.status === 'ENCRYPTING'
                          ? 'Encrypting...'
                          : file.status === 'FAILED'
                            ? 'Failed'
                            : ''}
                    </span>
                  </div>
                )}
            </div>
          </div>
          {/* Date column - Trashed at for trash mode, Created for normal mode */}
          <div className={` text-foreground/90 dark:text-white/90 text-sm col-span-2`}>
            {formattedDate}
          </div>

          {/* Size */}
          <div className="w-24  text-foreground/90 dark:text-white/90 text-sm col-span-2">
            {fileSize}
          </div>
          {/* Original Parent - only show in trash mode */}
          {trashMode && (
            <div className="">
              <PathTooltip
                fullContent={getParentFolderName(file.path).fullPath}
                title="Full Path"
                wide={true}
              >
                <div className="w-full  overflow-hidden">
                  <span className="text-foreground/90 dark:text-white/90 text-sm block truncate text-ellipsis whitespace-nowrap">
                    {truncateText(getParentFolderName(file.path).folderName, 20)}
                  </span>
                </div>
              </PathTooltip>
            </div>
          )}
          {/* Actions: hover bar overlays to the left of 3-dots */}
          <div
            className={`${trashMode ? 'w-16' : 'w-64'} flex items-center justify-end  absolute right-4`}
          >
            {(file.status === 'AVAILABLE' || file.isDir) &&
              (!isSystemFolder || trashMode || file.systemFolderType === 'CHAT_CONVERSATION') && (
                <div className="absolute right-0 mr-7 z-10  top-1/2 -translate-y-1/2 flex items-center gap-1 sm:gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-150 px-1 sm:px-2 py-1 bg-input-light/95 dark:bg-[#323232]/95 rounded-lg shadow-xl backdrop-blur-sm max-w-fit min-w-fit">
                  {trashMode ? (
                    <>
                      {/* Restore */}
                      <button
                        className="text-foreground/60 dark:text-white/60 hover:text-accent dark:hover:text-blue-400 transition-colors p-1"
                        title="Restore"
                        onClick={e => {
                          e.stopPropagation()
                          useActiveModelStore.setState({
                            width: 'max-w-[490px]',
                            modelActive: ModelControlEnum.CONFIRMATION,
                            data: {
                              title: `Restore "${file.name}"?`,
                              message:
                                'Are you sure you want to restore this file or folder from the trash?',
                              icon: '/assets/svg/warning.svg',
                              actions: [
                                {
                                  label: 'Cancel',
                                  color: 'default',
                                  variant: 'bordered',
                                  onPress: () => {
                                    closeAllModals()
                                    if (
                                      typeof window !== 'undefined' &&
                                      typeof window.dispatchEvent === 'function'
                                    ) {
                                      window.dispatchEvent(new Event('modal-close'))
                                    }
                                  },
                                },
                                {
                                  label: 'Restore',
                                  color: 'primary',
                                  variant: 'solid',
                                  onPress: async () => {
                                    await onRestore?.(file)
                                  },
                                },
                              ],
                            },
                            toggleModel: true,
                          })
                        }}
                      >
                        {/* Restore icon (same as context menu) */}
                        <svg
                          className="w-4 h-4 sm:w-5 sm:h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                          />
                        </svg>
                      </button>
                      {/* Delete Forever */}
                      <button
                        className="text-foreground/60 dark:text-white/60 hover:text-red-500 dark:hover:text-red-400 transition-colors p-1"
                        title="Delete Forever"
                        onClick={e => {
                          e.stopPropagation()
                          useActiveModelStore.setState({
                            width: 'max-w-[490px]',
                            modelActive: ModelControlEnum.CONFIRMATION,
                            data: {
                              title: `Delete "${file.name}" Forever?`,
                              message:
                                'This file or folder will be permanently deleted and cannot be restored.',
                              icon: '/assets/svg/warning.svg',
                              actions: [
                                {
                                  label: 'Cancel',
                                  variant: 'flat',
                                },
                                {
                                  label: 'Delete Forever',
                                  color: 'danger',
                                  variant: 'solid',
                                  onPress: async () => {
                                    await onDeleteForever?.(file)
                                  },
                                },
                              ],
                            },
                            toggleModel: true,
                          })
                        }}
                      >
                        <DeleteIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                      </button>
                    </>
                  ) : (
                    <>
                      {/* Download */}
                      {!file.isDir && (
                        <button
                          className={`text-foreground/60 dark:text-white/60 transition-colors p-1 ${file.systemFolderType === 'CHAT_CONVERSATION' && !trashMode ? 'cursor-not-allowed opacity-50' : 'hover:text-accent dark:hover:text-blue-400'}`}
                          disabled={
                            (!file.isDir && !onDownload) ||
                            (file.systemFolderType === 'CHAT_CONVERSATION' && !trashMode)
                          } // allow download for folders if handler exists
                          title={
                            file.systemFolderType === 'CHAT_CONVERSATION' && !trashMode
                              ? 'This action is disabled for conversation folders.'
                              : 'Download'
                          }
                          onClick={e => {
                            e.stopPropagation()
                            if (!(file.systemFolderType === 'CHAT_CONVERSATION' && !trashMode)) {
                              onDownload?.(file)
                            }
                          }}
                        >
                          <DownloadIconDrop className="w-4 h-4 sm:w-5 sm:h-5" />
                        </button>
                      )}
                      {/* Rename */}
                      <Tooltip
                        content="This action is disabled for conversation folders."
                        delay={200}
                        isDisabled={file.systemFolderType !== 'CHAT_CONVERSATION'}
                      >
                        <button
                          className={`text-foreground/60 dark:text-white/60 transition-colors p-1 ${file.systemFolderType === 'CHAT_CONVERSATION' && !trashMode ? 'cursor-not-allowed opacity-50' : 'hover:text-accent dark:hover:text-blue-400'}`}
                          disabled={file.systemFolderType === 'CHAT_CONVERSATION' && !trashMode}
                          title={
                            file.systemFolderType === 'CHAT_CONVERSATION' && !trashMode
                              ? ''
                              : 'Rename'
                          }
                          onClick={e => {
                            e.stopPropagation()
                            if (!(file.systemFolderType === 'CHAT_CONVERSATION' && !trashMode)) {
                              onRename?.(file)
                            }
                          }}
                        >
                          <RenameIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                        </button>
                      </Tooltip>
                      {/* Move */}
                      <Tooltip
                        content="This action is disabled for conversation folders."
                        delay={200}
                        isDisabled={file.systemFolderType !== 'CHAT_CONVERSATION'}
                      >
                        <button
                          className={`text-foreground/60 dark:text-white/60 transition-colors p-1 ${file.systemFolderType === 'CHAT_CONVERSATION' && !trashMode ? 'cursor-not-allowed opacity-50' : 'hover:text-accent dark:hover:text-blue-400'}`}
                          disabled={file.systemFolderType === 'CHAT_CONVERSATION' && !trashMode}
                          title={
                            file.systemFolderType === 'CHAT_CONVERSATION' && !trashMode
                              ? ''
                              : 'Move'
                          }
                          onClick={e => {
                            e.stopPropagation()
                            if (!(file.systemFolderType === 'CHAT_CONVERSATION' && !trashMode)) {
                              onMove?.(file)
                            }
                          }}
                        >
                          <MoveIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                        </button>
                      </Tooltip>
                      {/* Make a Copy (always show for folders and files) */}
                      {!file.isDir && (
                        <button
                          className={`text-foreground/60 dark:text-white/60 transition-colors p-1 ${
                            isOverQuota ||
                            (file.systemFolderType === 'CHAT_CONVERSATION' && !trashMode)
                              ? 'cursor-not-allowed opacity-50'
                              : 'hover:text-accent dark:hover:text-blue-400'
                          }`}
                          disabled={
                            isOverQuota ||
                            (file.systemFolderType === 'CHAT_CONVERSATION' && !trashMode)
                          }
                          title={
                            isOverQuota
                              ? 'Storage quota exceeded. Delete files to free up space.'
                              : file.systemFolderType === 'CHAT_CONVERSATION' && !trashMode
                                ? 'This action is disabled for conversation folders.'
                                : 'Make a Copy'
                          }
                          onClick={e => {
                            e.stopPropagation()
                            if (
                              !(
                                isOverQuota ||
                                (file.systemFolderType === 'CHAT_CONVERSATION' && !trashMode)
                              )
                            ) {
                              onCopy?.(file)
                            }
                          }}
                        >
                          <CopyIconDrop className="w-4 h-4 sm:w-5 sm:h-5" />
                        </button>
                      )}
                      {/* Star (Favorite) - filled if favorited, outline if not */}
                      <button
                        className={`transition-colors p-1 ${file.isFavorite ? 'text-yellow-400 dark:text-yellow-400' : 'text-foreground/60 dark:text-white/60 hover:text-yellow-400 dark:hover:text-yellow-400'}`}
                        title="Star"
                        onClick={e => {
                          e.stopPropagation()
                          onToggleFavorite?.(file)
                        }}
                      >
                        {file.isFavorite ? (
                          // Inline filled star SVG (if not in icons)
                          <svg
                            className="w-4 h-4 sm:w-5 sm:h-5"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path d="M9.9995 2.0835L7.45241 7.2825L1.66675 8.12145L5.85796 12.219L4.85608 17.9168L9.9995 15.1748L15.144 17.9168L14.1495 12.219L18.3334 8.12145L12.5798 7.2825L9.9995 2.0835Z" />
                          </svg>
                        ) : (
                          <StarIconMenu className="w-4 h-4 sm:w-5 sm:h-5" />
                        )}
                      </button>
                      {/* Delete */}
                      <Tooltip
                        content="This action is disabled for conversation folders."
                        delay={200}
                        isDisabled={file.systemFolderType !== 'CHAT_CONVERSATION'}
                      >
                        <button
                          className={`text-foreground/60 dark:text-white/60 transition-colors p-1 ${file.systemFolderType === 'CHAT_CONVERSATION' && !trashMode ? 'cursor-not-allowed opacity-50' : 'hover:text-red-500 dark:hover:text-red-400'}`}
                          disabled={file.systemFolderType === 'CHAT_CONVERSATION' && !trashMode}
                          title={
                            file.systemFolderType === 'CHAT_CONVERSATION' && !trashMode
                              ? ''
                              : 'Delete'
                          }
                          onClick={e => {
                            e.stopPropagation()
                            if (!(file.systemFolderType === 'CHAT_CONVERSATION' && !trashMode)) {
                              onDelete?.(file)
                            }
                          }}
                        >
                          <DeleteIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                        </button>
                      </Tooltip>
                    </>
                  )}
                </div>
              )}
            {/* Ghost file delete action - only visible on hover (exclude directories) */}
            {(file.status === 'UPLOADING' ||
              file.status === 'ENCRYPTING' ||
              file.status === 'FAILED') &&
              !file.isDir && (
                <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                  <button
                    className="text-foreground/60 dark:text-white/60 hover:text-red-500 dark:hover:text-red-400 transition-colors p-1"
                    title="Delete permanently"
                    onClick={e => {
                      e.stopPropagation()
                      // Use the same icon as cancelled upload status
                      useActiveModelStore.setState({
                        width: 'max-w-[490px]',
                        modelActive: ModelControlEnum.CONFIRMATION,
                        data: {
                          title: `Delete "${file.name}" Permanently?`,
                          message:
                            file.status === 'UPLOADING'
                              ? 'File upload may be in progress. Large files can take considerable time to process. Canceling now will permanently remove this file.'
                              : file.status === 'ENCRYPTING'
                                ? 'File encryption may be in progress. Large files can take considerable time to process. Canceling now will permanently remove this file.'
                                : 'File processing failed. This file can be permanently deleted.',
                          icon: '/assets/svg/warning.svg',
                          actions: [
                            {
                              label: 'Cancel',
                              color: 'default',
                              variant: 'flat',
                            },
                            {
                              label: 'Delete Permanently',
                              color: 'danger',
                              variant: 'solid',
                              onPress: async () => {
                                // If onDeleteGhostFiles is available, use it for ghost files
                                if (onDeleteGhostFiles) {
                                  await onDeleteGhostFiles([file.id.toString()])
                                } else {
                                  // Fallback to onDeleteForever for backward compatibility
                                  await onDeleteForever?.(file)
                                }
                              },
                            },
                          ],
                        },
                        toggleModel: true,
                      })
                    }}
                  >
                    <XCircleIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                  </button>
                </div>
              )}
            {/* 3-dots menu always visible at the end (except for non-directory uploading files and system folders, unless in trash mode) */}
            {(file.status !== 'UPLOADING' || file.isDir) && (!isSystemFolder || trashMode) && (
              <button
                className="text-foreground/60 dark:text-white/60 hover:text-accent dark:hover:text-blue-400 transition-colors p-1 flex-shrink-0 ml-2 relative z-10"
                tabIndex={0}
                onClick={e => {
                  e.stopPropagation()
                  handleContextMenu(e, file.path)
                }}
              >
                <MoreVertical className="w-5 h-5" size={20} />
              </button>
            )}
          </div>
          {shouldShowDropOverlay && (
            <div className="absolute inset-0 rounded-2xl border-2 border-accent border-dashed bg-accent-light/50 flex items-center justify-center dark:border-blue-400 dark:bg-blue-500/10" />
          )}
        </div>
        {/* Mobile/tablet view: card layout */}
        <div
          ref={handleListRef}
          className={`file-card flex justify-between lg:hidden flex-col gap-2 rounded-2xl px-4 py-3 w-full bg-input-light dark:bg-[#323232] transition-all duration-200 border-2 relative ${file.systemFolderType === 'CHAT_ROOT' ? 'opacity-60' : 'opacity-100'} ${getSelectionStateClasses()} ${getDragStateClasses()} ${getFolderDropZoneClasses()} ${!isInteractable ? 'select-none opacity-70' : ''} ${file.status === 'UPLOADING' || file.status === 'ENCRYPTING' || file.status === 'FAILED' ? 'opacity-80' : ''}`}
          draggable={isInteractable && !isSystemFolder}
          role="button"
          tabIndex={isInteractable ? 0 : -1}
          onClick={handleClick}
          onContextMenu={e => handleContextMenu(e, file.path)}
          onDoubleClick={handleDoubleClick}
          onKeyDown={e => {
            if (!isInteractable && (e.key === ' ' || e.key === 'Enter')) {
              e.preventDefault()
              e.stopPropagation()
            }
          }}
          onMouseDown={e => {
            if (!isInteractable) {
              e.preventDefault()
              e.stopPropagation()
            }
          }}
          {...rest}
        >
          {/* Processing overlay for unavailable files (excluding ghost files) */}
          {(file.status !== 'AVAILABLE' || file.isEncrypted === false) &&
            file.status !== 'UPLOADING' &&
            file.status !== 'ENCRYPTING' &&
            file.status !== 'FAILED' &&
            !file.isDir && (
              <ProcessingOverlay isEncrypted={file.isEncrypted} status={file.status} />
            )}

          <div className="flex items-center justify-between w-full overflow-hidden">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              {file.isDir ? (
                <FolderIcon className="sm:w-[30px] sm:h-[30px]" />
              ) : (
                <FileTypeIcon
                  className="sm:w-[30px] sm:h-[30px]"
                  filename={file.name}
                  mediaType={file.mediaType}
                  mimeType={file.mimeType}
                  size={24}
                />
              )}
              <div className="flex items-center gap-1 flex-1 min-w-0">
                <PathTooltip fullContent={file.name}>
                  <span className="text-foreground dark:text-white text-base font-medium truncate block overflow-hidden text-ellipsis whitespace-nowrap">
                    {file.name}
                  </span>
                </PathTooltip>
                {file.isFavorite && (
                  <div className="self-start pt-[4px]">
                    <svg
                      className="w-4 h-4 text-yellow-400 fill-current flex-shrink-0 inline ml-1"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9.9995 2.0835L7.45241 7.2825L1.66675 8.12145L5.85796 12.219L4.85608 17.9168L9.9995 15.1748L15.144 17.9168L14.1495 12.219L18.3334 8.12145L12.5798 7.2825L9.9995 2.0835Z" />
                    </svg>
                  </div>
                )}
              </div>
            </div>
            {/* Status and actions for mobile view */}
            <div className="flex items-center gap-2">
              {/* Ghost file status - show for UPLOADING, ENCRYPTING, and FAILED files (exclude directories) */}
              {(file.status === 'UPLOADING' ||
                file.status === 'ENCRYPTING' ||
                file.status === 'FAILED') &&
                !file.isDir && (
                  <div className="flex items-center gap-1 px-2 py-1 bg-primary-100 dark:bg-primary-900/30 rounded-md">
                    <div className="w-2 h-2 bg-primary-500 dark:bg-primary-400 rounded-full animate-pulse" />
                    <span className="text-xs font-medium text-primary-600 dark:text-primary-400">
                      {file.status === 'UPLOADING'
                        ? 'Uploading...'
                        : file.status === 'ENCRYPTING'
                          ? 'Encrypting...'
                          : file.status === 'FAILED'
                            ? 'Failed'
                            : ''}
                    </span>
                  </div>
                )}
              {/* 3-dots menu always visible at the end */}
              <button
                className="text-muted hover:text-accent transition-colors p-1 flex-shrink-0 dark:text-gray-400 dark:hover:text-white"
                tabIndex={0}
                onClick={e => {
                  e.stopPropagation()
                  handleContextMenu(e, file.path)
                }}
              >
                <MoreVertical className="w-5 h-5" size={20} />
              </button>
            </div>
          </div>
          <div className="flex items-center gap-6 mt-1">
            <span className="text-foreground/80 dark:text-white/80 text-sm text-nowrap">
              {formattedDate}
            </span>
            <span className="text-foreground/80 dark:text-white/80 text-sm text-nowrap">
              {fileSize}
            </span>
            {trashMode && (
              <PathTooltip fullContent={getParentFolderName(file.path).fullPath} title="Full Path">
                <div className="max-w-[80%] w-full overflow-hidden text-center">
                  <span className="text-foreground/80 dark:text-white/80 text-sm block truncate text-ellipsis whitespace-nowrap">
                    {`/${truncateText(getParentFolderName(file.path).folderName, 20)}`}
                  </span>
                </div>
              </PathTooltip>
            )}
          </div>
          {shouldShowDropOverlay && (
            <div className="absolute inset-0 rounded-2xl border-2 border-accent border-dashed bg-accent-light/50 flex items-center justify-center dark:border-blue-400 dark:bg-blue-500/10">
              <span className="text-accent font-medium text-sm dark:text-blue-400">
                Drop files to upload to this folder
              </span>
            </div>
          )}
        </div>
      </>
    )
  }

  // Default grid style
  return (
    <div
      ref={innerRef}
      className={`file-card bg-red-50 flex justify-between flex-col gap-3 sm:gap-8 relative rounded-xl p-3 cursor-pointer bg-white dark:bg-[#F1F4F920] transition-all duration-200 border-2 ${file.systemFolderType === 'CHAT_ROOT' ? 'opacity-60' : 'opacity-100'} ${getSelectionStateClasses()} ${getDragStateClasses()} ${getFolderDropZoneClasses()} ${!isInteractable ? 'select-none opacity-70' : ''} ${file.status === 'UPLOADING' || file.status === 'ENCRYPTING' || file.status === 'FAILED' ? 'opacity-80' : ''}`}
      draggable={isInteractable && !isSystemFolder}
      role="button"
      tabIndex={isInteractable ? 0 : -1}
      onClick={handleClick}
      onContextMenu={e => handleContextMenu(e, file.path)}
      onDoubleClick={handleDoubleClick}
      onKeyDown={e => {
        if (!isInteractable && (e.key === ' ' || e.key === 'Enter')) {
          e.preventDefault()
          e.stopPropagation()
        }
      }}
      onMouseDown={e => {
        if (!isInteractable) {
          e.preventDefault()
          e.stopPropagation()
        }
      }}
      {...rest}
    >
      {/* Processing overlay for unavailable files (excluding ghost files) */}
      {(file.status !== 'AVAILABLE' || file.isEncrypted === false) &&
        file.status !== 'UPLOADING' &&
        file.status !== 'ENCRYPTING' &&
        file.status !== 'FAILED' &&
        !file.isDir && <ProcessingOverlay isEncrypted={file.isEncrypted} status={file.status} />}

      <div className="flex items-start justify-between gap-[6px]">
        <div className="flex items-center gap-2 sm:gap-4 justify-start flex-1 min-w-0">
          {file.isDir ? (
            <FolderIcon className="sm:w-[30px] sm:h-[30px]" />
          ) : (
            <FileTypeIcon
              className="sm:w-[30px] sm:h-[30px]"
              filename={file.name}
              mediaType={file.mediaType}
              mimeType={file.mimeType}
              size={24}
            />
          )}
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <PathTooltip fullContent={file.name}>
              <h3
                className="text-foreground dark:text-white text-base font-medium leading-tight block overflow-hidden"
                style={{
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'normal',
                  wordBreak: 'break-all',
                  lineHeight: '1.2',
                  maxHeight: '2.4em', // 2 lines * 1.2em line-height
                }}
              >
                {file.name}
              </h3>
            </PathTooltip>
            {(file.status === 'UPLOADING' ||
              file.status === 'ENCRYPTING' ||
              file.status === 'FAILED') &&
              !file.isDir && (
                <div className="flex items-center gap-1 px-2 py-1 rounded-md flex-shrink-0">
                  <div
                    className={`w-2 h-2 rounded-full ${
                      file.status === 'UPLOADING'
                        ? 'bg-blue-500 dark:bg-blue-400 animate-pulse'
                        : file.status === 'ENCRYPTING'
                          ? 'bg-cyan-500 dark:bg-cyan-400 animate-pulse'
                          : 'bg-red-500 dark:bg-red-400'
                    }`}
                  />
                  <span
                    className={`text-xs font-medium ${
                      file.status === 'UPLOADING'
                        ? 'text-blue-600 dark:text-blue-300'
                        : file.status === 'ENCRYPTING'
                          ? 'text-cyan-600 dark:text-cyan-300'
                          : 'text-red-600 dark:text-red-300'
                    }`}
                  >
                    {file.status === 'UPLOADING'
                      ? 'Uploading...'
                      : file.status === 'ENCRYPTING'
                        ? 'Encrypting...'
                        : file.status === 'FAILED'
                          ? 'Failed'
                          : ''}
                  </span>
                </div>
              )}
          </div>
        </div>
        <div className="flex items-center">
          {file.isFavorite && (
            <Star className="w-4 h-4 sm:w-3 sm:h-3 text-yellow-400 fill-current flex-shrink-0" />
          )}
          {file.systemFolderType !== 'CHAT_ROOT' && (
            <button
              className="text-muted hover:text-accent transition-colors p-1 flex-shrink-0 dark:text-gray-400 dark:hover:text-white"
              tabIndex={0}
              onClick={e => {
                e.stopPropagation()
                handleContextMenu(e, file.path)
              }}
            >
              <MoreVertical className="sm:w-5 sm:h-5" size={16} />
            </button>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 text-foreground/90 dark:text-white/90 text-xs sm:text-sm">
        <span className="truncate">{fileSize}</span>
        <span className="truncate">{formattedDate}</span>
      </div>
      {shouldShowDropOverlay && (
        <div className="absolute inset-0 rounded-xl border-2 border-accent border-dashed bg-accent-light/50 flex items-center justify-center dark:border-blue-400 dark:bg-blue-500/10">
          <span className="text-accent font-medium text-sm dark:text-blue-400">
            Drop files to upload to this folder
          </span>
        </div>
      )}
    </div>
  )
}
